import { rollingWindowRange, todayUtc, yearToDateRange, type IsoDateRange } from "@acto/web-common/dates";
import { KpiComparisonMode } from "@acto/taskpane/core/domain";

/**
 * The analysis picker's period vocabulary, owned by the client. Deliberately decoupled from the
 * backend `StandardPeriod` enum: every preset resolves to an explicit `dateRange`
 * (see [resolvePeriodSelection]) and the backend only ever receives a window, never this identity.
 * That lets the UI offer presets the backend enum lacks (e.g. 24 months) with no backend change.
 */
export enum PeriodPreset {
  LAST_MONTH = "LAST_MONTH",
  LAST_3_MONTHS = "LAST_3_MONTHS",
  LAST_6_MONTHS = "LAST_6_MONTHS",
  LAST_12_MONTHS = "LAST_12_MONTHS",
  LAST_24_MONTHS = "LAST_24_MONTHS",
  YEAR_TO_DATE = "YEAR_TO_DATE",
}

export type PeriodSelection = { kind: "preset"; preset: PeriodPreset } | { kind: "custom"; range: IsoDateRange };

export const YEAR_TO_DATE_SELECTION: PeriodSelection = { kind: "preset", preset: PeriodPreset.YEAR_TO_DATE };
export const DEFAULT_PERIOD_SELECTION: PeriodSelection = YEAR_TO_DATE_SELECTION;

/** Year to date compares against last year whatever the tenant configured. */
export const comparisonModeFor = (selection: PeriodSelection, tenantMode: KpiComparisonMode): KpiComparisonMode =>
  selection.kind === "preset" && selection.preset === PeriodPreset.YEAR_TO_DATE
    ? KpiComparisonMode.YEAR_OVER_YEAR
    : tenantMode;

// Rolling windows count a month as 30 days, matching the backend's window resolution (api
// `KpiMappers.toDateRanges` / analytics `PredefinedTimeframe`): 1/3/6/12/24 months = 30/90/180/360/720 days.
const PRESET_WINDOW_DAYS: Record<Exclude<PeriodPreset, PeriodPreset.YEAR_TO_DATE>, number> = {
  [PeriodPreset.LAST_MONTH]: 30,
  [PeriodPreset.LAST_3_MONTHS]: 90,
  [PeriodPreset.LAST_6_MONTHS]: 180,
  [PeriodPreset.LAST_12_MONTHS]: 360,
  [PeriodPreset.LAST_24_MONTHS]: 720,
};

/** Resolves a preset to its `YYYY-MM-DD` window: rolling windows end yesterday, YTD starts Jan 1. */
export const presetDateRange = (preset: PeriodPreset, today: Date = todayUtc()): IsoDateRange =>
  preset === PeriodPreset.YEAR_TO_DATE ? yearToDateRange(today) : rollingWindowRange(PRESET_WINDOW_DAYS[preset], today);

/** Resolves a selection to its concrete query window; presets resolve client-side, never sent as an enum. */
export const resolvePeriodSelection = (selection: PeriodSelection, today?: Date): IsoDateRange =>
  selection.kind === "custom" ? selection.range : presetDateRange(selection.preset, today);
